/* eslint-disable react/require-default-props */
/// <reference types="googlemaps" />

import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { ReactSVG } from "react-svg";
import { useGoogleMaps } from "react-hook-google-maps";
import utils from "@helpers/utils";
import { intervalToDuration } from "date-fns";

// icons
import {
  InvoiceInstallmentStatus,
  TravelStatusCanceled,
  TravelStatusDelivered,
  TravelStatusDischarging,
  TravelStatusInConstruction,
  TravelStatusInTransit,
  TravelStatusPreparing,
  TravelStatusProgrammed,
  GpsSignal
} from "@assets/icons";

import _ from "lodash";

// languages
import useTranslation from "@languages/useTranslation";
import { TLanguages } from "@languages/languageData";

// types
import {
  TravelType,
  ETravelStatus
} from "@store/graphql/helpers/types.graphql";
import { BaseCardMessages } from "src/languages/interfaces";
import TravelTimeline from "../TravelTimeline";

import * as Styled from "./styles";

interface ITravel {
  id: string;
  status: string;
  concreteMixer?: {
    description: string;
    licensePlate: string;
  };
  driver?: {
    name: string;
  };
  loadLabel?: string;
  scheduledTime?: Date;
  dischargeStartTime?: Date;
  constructionEndTime?: Date;
  constructionStartTime?: Date;
  plantDepartureTime?: Date;
}

interface IIcons {
  [key: string]: {
    icon: string;
    color: string;
    labelStatus?: string;
    prefix?: string;
    labelData?: string;
  };
}

export interface MarkerOptions extends google.maps.MarkerOptions {
  id?: string;
}

export type TravelMarkerType = {
  travel: TravelType,
  marker: MarkerOptions,
  lastSendDate?: Date
}
interface MapProps {
  travelMarkers: TravelMarkerType[] | null;
  hasInfowindow?: boolean
}

const Map: React.FC<MapProps> = ({ travelMarkers, hasInfowindow = false }) => {

  const { t, i18n } = useTranslation();

  const GoogleApiKey = localStorage.getItem("@Topcon:google");
  const [stateMarkers, setStateMarkers] = useState<google.maps.Marker[]>([]);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(false);
  const { ref, map } = useGoogleMaps(
    `${GoogleApiKey}&libraries=places&language=pt-BR`,
    {
      center: {
        lat: -14.235004,
        lng: -51.92528
      },
      zoom: 16,
      mapTypeId: "roadmap"
    }
  );
  const nowDate = new Date();
  const travelInfoIcons: IIcons = {
    PROGRAMMED: {
      icon: TravelStatusProgrammed,
      color: "#64DD17",
      labelStatus: t(BaseCardMessages.statusProgrammedLabelText),
      prefix: t(BaseCardMessages.statusProgrammedPrefixText)
    },
    PREPARING: {
      icon: TravelStatusPreparing,
      color: "#ED7117",
      labelStatus: t(BaseCardMessages.statusPreparingLabelText)
    },
    IN_TRANSIT: {
      icon: TravelStatusInTransit,
      color: "#7E73F8",
      labelStatus: t(BaseCardMessages.statusInTransitLabelText),
      prefix: t(BaseCardMessages.statusInTransitPrefixText)
    },
    IN_CONSTRUCTION: {
      icon: TravelStatusInConstruction,
      color: "#C7B301",
      labelStatus: t(BaseCardMessages.statusInConstructionLabelText),
      prefix: t(BaseCardMessages.statusInConstructionPrefixText),
      labelData: `${t(
        BaseCardMessages.statusInConstructionDescriptionText
      )}:`
    },
    DISCHARGING: {
      icon: TravelStatusDischarging,
      color: "#616161",
      labelStatus: t(BaseCardMessages.statusDischargingLabelText),
      prefix: t(BaseCardMessages.statusDischargingPrefixText),
      labelData: `${t(BaseCardMessages.statusDischargingDescriptionText)}:`
    },
    DELIVERED: {
      icon: TravelStatusDelivered,
      color: "#64DD17",
      labelStatus: t(BaseCardMessages.statusDeliveredLabelText),
      labelData: `${t(BaseCardMessages.statusDeliveredDescriptionText)}:`
    },
    CANCELED: {
      icon: TravelStatusCanceled,
      color: "#FF0000",
      labelStatus: t(BaseCardMessages.statusCanceledLabelText)
    },
    INVOICE_ISSUED: {
      icon: InvoiceInstallmentStatus,
      color: "#64DD17",
      labelStatus: t(BaseCardMessages.statusInvoiceIssuedLabelText),
      prefix: t(BaseCardMessages.statusInvoiceIssuedPrefixText)
    }
  };

  const lastSendDateContent = useCallback((travelMarker: TravelMarkerType) => {
    if (travelMarker.lastSendDate) {
      switch (i18n.language) {
        case "es":
          return (
            <>
              {t(BaseCardMessages.mapTextLastUpdate)}
              {utils.formatDateIfHave(new Date(travelMarker.lastSendDate), "dateWithoutYear")}
              {" - "}
              {t(BaseCardMessages.mapTextAgo)}
              {utils.formatDateIfHave(
                intervalToDuration({
                  start: new Date(travelMarker.lastSendDate),
                  end: new Date()
                }),
                "durationDescriptiveTime"
              )}
            </>
          );
        case "en-US":
          return (
            <>
              {t(BaseCardMessages.mapTextLastUpdate)}
              {utils.formatDateIfHave(new Date(travelMarker.lastSendDate), "dateWithoutYear")}
              {" - "}
              {utils.formatDateIfHave(
                intervalToDuration({
                  start: new Date(travelMarker.lastSendDate),
                  end: new Date()
                }),
                "durationDescriptiveTime"
              )}
              {t(BaseCardMessages.mapTextAgo)}

            </>
          );

        default:
          return (
            <>
              {t(BaseCardMessages.mapTextLastUpdate)}
              {utils.formatDateIfHave(new Date(travelMarker.lastSendDate), "dateWithoutYear")}
              {" - à "}
              {utils.formatDateIfHave(
                intervalToDuration({
                  start: new Date(travelMarker.lastSendDate),
                  end: new Date()
                }),
                "durationDescriptiveTime"
              )}
              {t(BaseCardMessages.mapTextAgo)}

            </>
          );

      }

    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return (<></>);
  }, [i18n, travelMarkers]);

  /**
   * Get content of travel
   * @param travel Travel content
   */
  const getContentTravel = (travel: TravelType) => {

    if (!travel) return null;

    const programmedTime = travel.scheduledTime
      && utils.formatDateIfHave(travel.scheduledTime, "timeWithoutSeconds");
    const showCompleteData = travel.status === "IN_TRANSIT"
      || travel.status === "IN_CONSTRUCTION"
      || travel.status === "DISCHARGING"
      || travel.status === "DELIVERED"
      || travel.status === "INVOICE_ISSUED";
    const showProgrammedTime = travel.status === "PREPARING" || travel.status === "CANCELED";
    const showTimeStatus = travel.status !== "CANCELED" && travel.status !== "PREPARING";

    let timeStatus;
    let timeStartStatus;

    switch (travel.status) {
      case ETravelStatus.INVOICE_ISSUED:
        if (travel.invoiceIssueTime) {
          timeStatus = utils.formatDateIfHave(
            new Date(travel.invoiceIssueTime),
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.IN_TRANSIT:
        if (travel.plantDepartureTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.plantDepartureTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      case ETravelStatus.IN_CONSTRUCTION:
        if (travel.constructionStartTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.constructionStartTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            new Date(travel.constructionStartTime),
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.DISCHARGING:
        if (travel.dischargeStartTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.dischargeStartTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            new Date(travel.dischargeStartTime),
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.DELIVERED:

        if (travel.plantDepartureTime) {

          const startTime = new Date(travel.plantDepartureTime ?? travel.invoiceIssueTime);
          let endTime = new Date(travel.invoiceIssueTime ?? travel.plantDepartureTime);

          if (travel.dischargeEndTime) endTime = new Date(travel.dischargeEndTime);
          else if (travel.constructionEndTime) endTime = new Date(travel.constructionEndTime);
          else if (travel.finishTime) endTime = new Date(travel.finishTime);

          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: startTime,
              end: endTime
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            endTime,
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.PROGRAMMED:

        if (travel.scheduledTime) {

          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: nowDate,
              end: new Date(travel.scheduledTime)
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      default:
        break;
    }

    let prefix = "";
    let suffix = "";

    if (showTimeStatus) {

      if (travel.status !== "DELIVERED") {
        prefix = travelInfoIcons[travel.status]?.prefix ?? "";
      }

      if (travel.status === "PROGRAMMED" && nowDate.getTime() > new Date(travel.scheduledTime).getTime()) {
        prefix = "para";
        suffix = " atrás";
      }
    }

    return (
      <div>
        {showCompleteData && (
        <div className="inlineContent">
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.travelEquipmentText)}`}:{" "}
            </span>
            {travel.concreteMixer?.code || travel.concreteMixer?.description}
          </div>
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.travelPlateText)}`}:{" "}
            </span>
            {travel.concreteMixer?.licensePlate}
          </div>
        </div>
        )}
        {showCompleteData && travel.driver && (
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.travelDriverText)}`}:{" "}
          </span>
          {travel.driver.name}
        </div>
        )}
        <div>
          {showProgrammedTime ? (
            <>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelProgrammedText)}`}:{" "}
              </span>
              {programmedTime}
            </>
          ) : (
            <>
              <span className="titleContent">
                {travelInfoIcons[travel.status].labelData}{" "}
              </span>
              {timeStartStatus}
            </>
          )}
        </div>
        {showCompleteData && (
        <div>
          <div className="inlineContent">
            <div>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelDocumentNumberText)}`}:{" "}
              </span>
              {travel.documentNumber}
            </div>
            <div>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelLoadLabelText)}`}:{" "}
              </span>
              {travel.loadLabel}
            </div>
          </div>
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.volumeText)}: `}
            </span>
            {travel.volume}
          </div>
        </div>
        )}
        <div>
          <span className="contentColor">
            {travelInfoIcons[travel.status].labelStatus}
          </span>
          {showTimeStatus && ` ${prefix} ${timeStatus}${suffix}`}
        </div>
      </div>
    );
  };

  // Remove markers from map
  const clearMarkers = useCallback(() => {
    setStateMarkers((state) => state
      .map((mark) => {
        mark.setVisible(false);

        if (mark["infowindow"]) {
          mark["infowindow"].close();
          mark["infowindow"].setMap(null);
          // eslint-disable-next-line no-param-reassign
          mark["infowindow"] = null;
        }

        mark.setMap(null);

        return mark;
      })
      .filter((f) => false));
  }, [setStateMarkers]);

  /** Pin a marker in map
   * @param marker Marker to be pinned in the map
   */
  const placeMarker = useCallback(
    (travelMarker: TravelMarkerType) => {

      if (!_.isEmpty(travelMarker)) {

        const instancedMarker = new google.maps.Marker({
          ...travelMarker.marker,
          map
        });

        const { travel } = travelMarker;

        if (hasInfowindow) {

          const contentTravel = getContentTravel(travel);

          // Instance the info window
          instancedMarker["infowindow"] = new google.maps.InfoWindow();

          // Create click event with info window
          google.maps.event.addListener(instancedMarker, "click", ((marker, content, infowindow) => () => {

            instancedMarker["infowindow"].setContent(`<div id="infoWindow-marker-${travelMarker.travel.id}"></div>`);
            instancedMarker["infowindow"].open(map, marker);

            setTimeout(() => {
              ReactDOM.render(
                <Styled.WindowContainer>
                  <Styled.InfoContainer color={travelInfoIcons[travel.status ?? ""].color}>

                    <Styled.IconContainer color={travelInfoIcons[travel.status ?? ""].color}>
                      <ReactSVG src={travelInfoIcons[travel.status ?? ""].icon} />
                    </Styled.IconContainer>
                    {contentTravel}
                  </Styled.InfoContainer>

                  <TravelTimeline travel={travel} />
                  {
                    travelMarker.lastSendDate && (
                    <div className="communication">
                      <ReactSVG src={GpsSignal} />
                      {
                        lastSendDateContent(travelMarker)
                      }
                    </div>

                    )
                  }
                </Styled.WindowContainer>,
                document.getElementById(`infoWindow-marker-${travelMarker.travel.id}`)
              );

            }, 0);
          })(instancedMarker));
        }

        setStateMarkers((res) => [...res, instancedMarker]);
      }
    },
    [map, travelMarkers]
  );

  // Init map, places Markers, Polygons and Circles
  useEffect(() => {

    if (map) {
      const bounds = new google.maps.LatLngBounds();

      // Place Markers on map
      if (travelMarkers) {
        travelMarkers.forEach((travelMarker) => {
          placeMarker(travelMarker);
          bounds.extend(travelMarker.marker.position as google.maps.LatLng);
        });
      }

      if (!isFirstRender && travelMarkers && travelMarkers.length > 0) {
        map.fitBounds(bounds);
        setIsFirstRender(true);
      }
    }

    return () => {
      clearMarkers();
    };

  }, [clearMarkers, map, travelMarkers, placeMarker]);

  return (
    <Styled.Container>
      <div
        className="map"
        ref={ref}
        style={{ width: "100%", height: "100%" }}
      />
    </Styled.Container>
  );
};

export default Map;